/**
 * URL helpers for classifying internal vs external links and applying nofollow.
 */

import WealthyEnv from "./env";

const INTERNAL_HOST_SUFFIXES = [
  "wealthy.in",
  "wealthydev.in",
  "buildwealth.in",
  "buildwealthdev.in",
  "app.buildwealth.in",
  "app.buildwealthdev.in",
  "wlycdn.com",
  "wealthyinsurance.in",
  "wealthyinsurancedev.in",
  "grage.in",
  "gragedev.in",
  "wlthy.in"
];

const SKIP_PROTOCOLS = ["mailto:", "tel:", "javascript:", "data:"];

const isInternalHost = (hostname) => {
  if (!hostname) {
    return true;
  }

  const host = hostname.toLowerCase();

  return INTERNAL_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`)
  );
};

export const isInternalUrl = (href, baseUrl = WealthyEnv.URL) => {
  if (!href || typeof href !== "string") {
    return true;
  }

  const trimmed = href.trim();

  if (!trimmed || trimmed === "#") {
    return true;
  }

  if (SKIP_PROTOCOLS.some((protocol) => trimmed.toLowerCase().startsWith(protocol))) {
    return true;
  }

  if (trimmed.startsWith("#")) {
    return true;
  }

  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return true;
  }

  try {
    const url = new URL(trimmed, baseUrl);
    return isInternalHost(url.hostname);
  } catch {
    return true;
  }
};

export const mergeExternalLinkRel = (existingRel = "") => {
  const rels = new Set(
    (existingRel || "")
      .split(/\s+/)
      .filter(Boolean)
      .map((rel) => rel.toLowerCase())
  );

  rels.add("nofollow");
  rels.add("noopener");
  rels.add("noreferrer");

  return [...rels].join(" ");
};

export const applyExternalLinkAttributes = (anchor) => {
  if (!anchor || anchor.nodeName !== "A") {
    return;
  }

  const href = anchor.getAttribute("href");

  if (!href || isInternalUrl(href)) {
    return;
  }

  anchor.setAttribute("rel", mergeExternalLinkRel(anchor.getAttribute("rel")));

  if (!anchor.getAttribute("target")) {
    anchor.setAttribute("target", "_blank");
  }

  anchor.setAttribute("data-external-link-processed", "true");
};

export const getExternalLinkProps = (href, props = {}) => {
  if (!href || isInternalUrl(href)) {
    return {href, ...props};
  }

  const {rel, target, ...rest} = props;

  return {
    ...rest,
    href,
    rel: mergeExternalLinkRel(rel),
    target: target || "_blank"
  };
};

export const processExternalLinks = (root) => {
  if (!root?.querySelectorAll) {
    return;
  }

  root
    .querySelectorAll('a[href]:not([data-external-link-processed])')
    .forEach(applyExternalLinkAttributes);
};
